import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { trackEvent, trackPageView, trackScrollDepth, trackTimeOnPage, trackUtmEntry, trackVideoPlay, trackVideoMilestone, trackVideoComplete, trackReturnVisit, setPersonaDimension, setVisitorTypeDimension, incrementSessionDepth, trackEmailClick } from '../analytics';
import type { SxProps } from '@mui/material';

// ── LP tracking hook — page view + UTM + scroll + time + return visit + persona ──
export function useLpTracking(pagePath: string) {
  useEffect(() => {
    trackPageView(pagePath);
    trackUtmEntry(pagePath);
    setPersonaDimension(pagePath);
    setVisitorTypeDimension();
    incrementSessionDepth();

    const visited = localStorage.getItem('smcla_visited');
    if (visited) trackReturnVisit(pagePath);

    const fired = new Set<number>();
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = Math.round((window.scrollY / scrollable) * 100);
      for (const m of [25, 50, 75, 90]) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          trackScrollDepth(m, pagePath);
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    const timers = [
      setTimeout(() => trackTimeOnPage(30, pagePath), 30_000),
      setTimeout(() => trackTimeOnPage(60, pagePath), 60_000),
      setTimeout(() => trackTimeOnPage(120, pagePath), 120_000),
    ];

    return () => {
      window.removeEventListener('scroll', onScroll);
      timers.forEach(clearTimeout);
    };
  }, [pagePath]);
}

// ── Minimal LP header (no nav — conversion focused) ──────────────────────────
export function LpHeader({ ctaLabel = 'Book a Demo', ctaPath = '/contact', pagePath = '/' }: { ctaLabel?: string; ctaPath?: string; pagePath?: string }) {
  const navigate = useNavigate();
  return (
    <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 1200, bgcolor: 'rgba(10,10,18,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)', px: { xs: 2, md: 4 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box component={RouterLink} to="/" sx={{ textDecoration: 'none' }}>
        <img src="/logo.png" alt="Smart Media Clarity" style={{ maxHeight: 36, maxWidth: 120, width: 'auto', display: 'block' }} />
      </Box>
      <Button
        variant="contained"
        size="small"
        onClick={() => { trackEvent('cta_click', ctaLabel, pagePath, { location: 'lp_header' }); navigate(ctaPath); }}
        sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, px: 2.5, py: 0.75, '&:hover': { bgcolor: '#4ecfd4' } }}
      >
        {ctaLabel} →
      </Button>
    </Box>
  );
}

// ── Minimal LP footer ─────────────────────────────────────────────────────────
export function LpFooter() {
  return (
    <Box component="footer" sx={{ bgcolor: '#0a0a12', borderTop: '1px solid rgba(255,255,255,0.06)', py: 3, px: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.5)' }}>
        © {new Date().getFullYear()} Smart Media Clarity. All rights reserved.
      </Typography>
      <Stack direction="row" spacing={3}>
        <Box component={RouterLink} to="/privacy-policy" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.5)', textDecoration: 'none', '&:hover': { color: '#9499b0' } }}>Privacy Policy</Box>
        <Box component="a" href="mailto:admin@smartmediaclarity.com" onClick={() => trackEmailClick(window.location.pathname, 'lp_footer')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.5)', textDecoration: 'none', '&:hover': { color: '#9499b0' } }}>Contact</Box>
      </Stack>
    </Box>
  );
}

// ── Reusable "Is this you?" section ─────────────────────────────────────────
export function IsThisYou({ items }: { items: string[] }) {
  return (
    <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.07)', borderLeft: '3px solid rgba(92,225,230,0.4)', borderRadius: 3 }}>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>
        Is this you?
      </Typography>
      <Stack spacing={1.5}>
        {items.map((item) => (
          <Box key={item} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Typography sx={{ color: '#5ce1e6', fontSize: 16, lineHeight: 1.4, flexShrink: 0 }}>—</Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#d0d0e0', lineHeight: 1.6 }}>{item}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ── Reusable bottom CTA block ─────────────────────────────────────────────────
export function LpCtaBlock({ headline, sub, ctaLabel, ctaPath, pagePath }: { headline: string; sub: string; ctaLabel: string; ctaPath: string; pagePath: string }) {
  const navigate = useNavigate();
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, px: 3, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.12) 0%, transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 540, mx: 'auto' }}>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', lineHeight: 1.15, mb: 2 }}>
          {headline}
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', lineHeight: 1.65, mb: 5 }}>
          {sub}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => { trackEvent('cta_click', ctaLabel, pagePath, { location: 'lp_cta_block' }); navigate(ctaPath); }}
          sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 17, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 32px rgba(92,225,230,0.3)', '&:hover': { bgcolor: '#4ecfd4', boxShadow: '0 0 40px rgba(92,225,230,0.4)' } }}
        >
          {ctaLabel} →
        </Button>
      </Box>
    </Box>
  );
}

// ── Shared scroll-fade hook ───────────────────────────────────────────────────
export function useScrollFade(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export const fadeStyle = (inView: boolean, delay = 0): SxProps => ({
  opacity: inView ? 1 : 0,
  transform: inView ? 'translateY(0)' : 'translateY(28px)',
  transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
});

// ── Video tracking hook ───────────────────────────────────────────────────────
export function useVideoTracking(videoRef: React.RefObject<HTMLVideoElement | null>, videoName: string, pagePath: string) {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let played = false;
    const milestonesHit = new Set<number>();

    const onPlay = () => {
      if (!played) {
        played = true;
        trackVideoPlay(videoName, pagePath);
      }
    };

    const onTimeUpdate = () => {
      if (!video.duration) return;
      const pct = (video.currentTime / video.duration) * 100;
      for (const m of [25, 50]) {
        if (pct >= m && !milestonesHit.has(m)) {
          milestonesHit.add(m);
          trackVideoMilestone(videoName, m, pagePath);
        }
      }
      if (pct >= 75 && !milestonesHit.has(75)) {
        milestonesHit.add(75);
        trackVideoComplete(videoName, pagePath);
      }
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [videoRef, videoName, pagePath]);
}
